import React, { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

interface NavigationHandlerProps {
  children: React.ReactNode;
}

const NavigationHandler: React.FC<NavigationHandlerProps> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const navigationStack = useRef<string[]>([]);
  const isNavigating = useRef(false);

  useEffect(() => {
    // Track navigation stack
    if (!isNavigating.current) {
      navigationStack.current.push(location.pathname);
    }
    isNavigating.current = false;

    // Handle hardware back button
    const handlePopState = () => {
      if (navigationStack.current.length > 1) {
        isNavigating.current = true;
        navigationStack.current.pop();
        navigate(-1);
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [location, navigate]);

  // Handle gesture-based navigation
  useEffect(() => {
    let touchStartX = 0;
    const threshold = 100;

    const handleTouchStart = (e: TouchEvent) => {
      touchStartX = e.touches[0].clientX;
    };

    const handleTouchEnd = (e: TouchEvent) => {
      const touchEndX = e.changedTouches[0].clientX;
      const diff = touchEndX - touchStartX;

      if (diff > threshold && navigationStack.current.length > 1) {
        isNavigating.current = true;
        navigationStack.current.pop();
        navigate(-1);
      }
    };

    document.addEventListener('touchstart', handleTouchStart);
    document.addEventListener('touchend', handleTouchEnd);

    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [navigate]);

  return <>{children}</>;
};

export default NavigationHandler;